import { createContext, useContext, useState, useRef } from 'react';
import { isNullish } from '@sas/nova-core/typeguards';

const HtmlIdPrefixContext = createContext<string>('sas_RC');

/**
 * Generates a unique id string. Supplying a number argument to the hook will generate
 * that number of unique string ids returned in an array.
 *
 * @param component The name of the component, used to uniquely generate the id(s).
 *
 * @example
 * ```
 * const useHtmlId = createUseHtmlId('MyInputComponent');
 *
 * function MyInputComponent(props) {
 *  const id = useHtmlId();
 *  return <input id={id} />;
 * }
 *
 * const useHtmlIds = createUseHtmlId('MyMultiInputComponent');
 *
 * function MyMultiInputComponent(props) {
 *  const ids = useHtmlIds(3);
 *  return (
 *    <div>
 *      <input id={ids[0]} />
 *      <input id={ids[1]} />
 *      <input id={ids[2]} />
 *    </div>
 *  );
 * }
 * ```
 */

export function createUseHtmlId(component: string) {
  let i = 0;

  function formatId(prefix: string) {
    return `${prefix}-${component}-${i++}`;
  }

  function useId(): string;
  function useId(numberOfIds: number): string[];
  function useId(numberOfIds?: number): string | string[] {
    const prefix = useContext(HtmlIdPrefixContext);
    const refId = useRef<string>();
    const [ids, setIds] = useState<string[]>();

    if (isNullish(numberOfIds)) {
      if (isNullish(refId.current)) {
        refId.current = formatId(prefix);
      }
      return refId.current;
    } else {
      if (ids && ids.length === numberOfIds) {
        return ids;
      }
      const newIds = ids ? [...ids] : [];
      {
        // Add additional ids
        for (let index = newIds.length; index < numberOfIds; index++) {
          newIds.push(formatId(prefix));
        }
        // Remove extra ids
        newIds.length = numberOfIds;
      }
      setIds(newIds);
      return newIds;
    }
  }
  return useId;
}
